/**
 * Export Edited Network → BODS (TransXChange) Modal (PRD-05b)
 *
 * Server-side, streamed export of a hosted network with the drawer's edits merged in.
 * Unlike `ExportBodsModal` (1A, client-side, the drawer's own routes), this POSTs an
 * **overlay** (`buildNetworkExportOverlay`) to the backend, which merges it into the WHOLE
 * hosted feed (`scope='full'`) — far too large for the browser — and streams back a
 * per-service TransXChange `.zip`. A `scope` toggle offers "full network with edits" vs
 * "changed/new routes only". Operators are carried through from the feed where they exist;
 * the operator fields here are the single fallback for routes without one.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@liftango/ui-icons';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { useNetworkBrowse } from '../../context/NetworkBrowseContext';
import { buildNetworkExportOverlay } from '../../utils/fixedRoutes/networkExportOverlay';
import {
  exportEditedNetwork,
  type NetworkExportScope,
  type NetworkExportStatus,
} from '../../services/network/networkApi';
import { downloadBlob } from '../../utils/fixedRoutes/exportHelpers';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { formatDateAsWallTime } from '../../utils/dateTimeUtils';
import type { ServiceDaysMask } from '../../types/fixedRoutes';

interface ExportEditedNetworkModalProps {
  open: boolean;
  onClose: () => void;
}

const DAY_FIELDS: Array<{ key: keyof ServiceDaysMask; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const WEEKDAY_MASK: ServiceDaysMask = {
  monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
  saturday: false, sunday: false,
};

const plusOneYear = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-');
  return `${String(Number(y) + 1).padStart(4, '0')}-${m}-${d}`;
};

export function ExportEditedNetworkModal({ open, onClose }: ExportEditedNetworkModalProps) {
  const { t } = useTranslation('fixedRoutes');
  const { stops, routes, trafficMultiplier } = useFixedRoutes();
  const { feedId, version } = useNetworkBrowse();

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London', []);

  const [scope, setScope] = useState<NetworkExportScope>('full');
  const [feedName, setFeedName] = useState(feedId || 'network');
  const [noc, setNoc] = useState('');
  const [operatorName, setOperatorName] = useState('');
  const [licenceNumber, setLicenceNumber] = useState('');
  const [days, setDays] = useState<ServiceDaysMask>(WEEKDAY_MASK);
  const [startDate, setStartDate] = useState(DEFAULT_SIMULATION_DATE);
  const [endDate, setEndDate] = useState(() => plusOneYear(DEFAULT_SIMULATION_DATE));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<NetworkExportStatus | null>(null);

  // Pre-flight overlay (counts of edited/new routes + from-scratch stops). Cheap; memoised.
  const overlayResult = useMemo(() => {
    if (!feedId) return null;
    return buildNetworkExportOverlay({ stops, routes, trafficMultiplier }, feedId, {
      defaultServiceDays: days,
      defaultServiceDateRange: { startDate, endDate },
      meta: {
        feedName,
        agencyName: operatorName || feedName,
        agencyUrl: 'https://liftango.com',
        agencyTimezone: timezone,
        agencyLang: 'en',
        feedVersion: formatDateAsWallTime(new Date()).slice(0, 19),
      },
    });
  }, [feedId, stops, routes, trafficMultiplier, days, startDate, endDate, feedName, operatorName, timezone]);

  const editedCount = overlayResult?.editedCount ?? 0;
  const newCount = overlayResult?.newCount ?? 0;
  const stopsWithoutAtco = overlayResult?.stopsWithoutAtco ?? 0;
  // 'changed' needs at least one edit; 'full' is always valid (re-emits the whole feed).
  const hasEdits = editedCount + newCount > 0;
  const canDownload = !!feedId && !busy && (scope === 'full' || hasEdits);

  const setDay = (key: keyof ServiceDaysMask, value: boolean) =>
    setDays((prev) => ({ ...prev, [key]: value }));

  const handleExport = async () => {
    if (!feedId || !overlayResult) return;
    const startedAt = Date.now();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { blob, filename, status: finalStatus } = await exportEditedNetwork(
        feedId,
        version ?? undefined,
        overlayResult.overlay,
        {
          scope,
          operator: { nationalOperatorCode: noc || undefined, operatorName: operatorName || undefined, licenceNumber: licenceNumber || undefined },
          feedName,
          creationDateTime: new Date().toISOString().slice(0, 19),
          defaultStartDate: startDate,
          defaultEndDate: endDate,
          onProgress: setStatus,
        },
      );
      downloadBlob(blob, filename);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.bods_network_export, {
        feed_id: feedId,
        scope,
        route_count: finalStatus.routesWritten ?? finalStatus.total ?? 0,
        edited_count: finalStatus.editedCount ?? editedCount,
        new_count: finalStatus.newCount ?? newCount,
        stops_without_atco: finalStatus.counts?.stopsWithoutAtco ?? stopsWithoutAtco,
        duration_ms: Date.now() - startedAt,
        timestamp: new Date().toISOString(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('networkExport.errorBody', 'Export failed'));
    } finally {
      setBusy(false);
    }
  };

  const progressPct =
    status?.total && status.total > 0 ? Math.round(((status.routesWritten ?? 0) / status.total) * 100) : null;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="bus" size={20} />
          {t('networkExport.title', 'Export edited network → BODS')}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'networkExport.description',
              'Export the whole hosted network with your edits merged in, as a UK BODS-publishable TransXChange dataset. The merge + file run on the server (the full network is too large for the browser); download the .zip and upload it to your BODS operator account.',
            )}
          </Typography>

          {!feedId && (
            <Alert severity="info" icon={<Icon name="circle-info" size={20} />}>
              {t('networkExport.noFeed', 'Open a hosted network in the Network tab first.')}
            </Alert>
          )}

          {/* Scope */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Icon name="list" size={18} />
              <Typography variant="subtitle2">{t('networkExport.scope', 'What to export')}</Typography>
            </Box>
            <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as NetworkExportScope)}>
              <FormControlLabel
                value="full"
                control={<Radio size="small" />}
                label={t('networkExport.scopeFull', 'Full network with my edits merged in')}
              />
              <FormControlLabel
                value="changed"
                control={<Radio size="small" />}
                disabled={!hasEdits}
                label={t('networkExport.scopeChanged', 'Only my changed / new routes')}
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {t('networkExport.editSummary', {
                defaultValue: '{{edited}} edited · {{added}} new route(s) in your working set',
                edited: editedCount,
                added: newCount,
              })}
            </Typography>
          </Box>

          <Divider />

          {/* Operator (fallback) */}
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="flag" size={18} />
              <Typography variant="subtitle2">{t('networkExport.operator', 'Export operator (fallback)')}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t(
                'networkExport.operatorHint',
                "Operators are carried through from the feed where known. These fields apply to routes that don't have one (e.g. routes you drew).",
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('networkExport.noc', 'National Operator Code (NOC)')}
                value={noc}
                onChange={(e) => setNoc(e.target.value)}
                size="small"
                sx={{ width: 220 }}
              />
              <TextField
                label={t('networkExport.operatorName', 'Operator name')}
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
            <TextField
              label={t('networkExport.licence', 'Operator licence number (optional)')}
              value={licenceNumber}
              onChange={(e) => setLicenceNumber(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>

          <Divider />

          {/* Feed metadata */}
          <TextField
            label={t('networkExport.feedName', 'Feed name')}
            value={feedName}
            onChange={(e) => setFeedName(e.target.value)}
            size="small"
            fullWidth
          />

          <Divider />

          {/* Fallback calendar */}
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="clock" size={18} />
              <Typography variant="subtitle2">{t('networkExport.fallbackCalendar', 'Fallback service calendar')}</Typography>
            </Box>
            <FormGroup row>
              {DAY_FIELDS.map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={<Checkbox size="small" checked={days[key]} onChange={(e) => setDay(key, e.target.checked)} />}
                  label={label}
                />
              ))}
            </FormGroup>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label={t('networkExport.startDate', 'Start date')}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label={t('networkExport.endDate', 'End date')}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Stack>

          {/* NaPTAN pre-flight warning */}
          {stopsWithoutAtco > 0 && (
            <Alert severity="warning" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>
                {t('networkExport.atcoWarnTitle', {
                  defaultValue: '{{count}} stop(s) not NaPTAN-matched',
                  count: stopsWithoutAtco,
                })}
              </AlertTitle>
              {t(
                'networkExport.atcoWarnBody',
                'Stops you drew from scratch have no NaPTAN ATCO code and export with a synthetic reference. The file is valid but not registration-grade until those stops are matched to NaPTAN.',
              )}
            </Alert>
          )}

          {/* Progress */}
          {busy && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Icon name="spinner" size={16} />
                <Typography variant="body2" color="text.secondary">
                  {status?.step === 'writing'
                    ? t('networkExport.progressWriting', {
                        defaultValue: 'Writing routes {{done}} / {{total}}…',
                        done: status?.routesWritten ?? 0,
                        total: status?.total ?? 0,
                      })
                    : t('networkExport.progressStarting', 'Preparing the export…')}
                </Typography>
              </Box>
              <LinearProgress variant={progressPct == null ? 'indeterminate' : 'determinate'} value={progressPct ?? 0} />
            </Box>
          )}

          {/* Post-export server warnings */}
          {status?.warnings && status.warnings.length > 0 && (
            <Alert severity="warning" icon={<Icon name="triangle-exclamation" size={20} />}>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {status.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </Box>
            </Alert>
          )}

          {error && (
            <Alert severity="error" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>{t('networkExport.errorTitle', 'Export failed')}</AlertTitle>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="file-arrow-down" size={20} />}
          onClick={handleExport}
          disabled={!canDownload}
        >
          {t('networkExport.download', 'Export edited network')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
